import './AvailableEntries.scss';
import './scrollbar.scss';
import type { Entry } from './types';
import { Entries } from './Entries';
import { _ } from '../../Lang.class.js';

type AvailableEntriesCallbacks = {
    onClickEntry: (entry: Entry) => void;
};

export class AvailableEntries extends Entries {
    private readonly root: HTMLDivElement;
    private readonly callbacks: AvailableEntriesCallbacks;
    private currentSearchString: string = '';

    constructor(root: HTMLDivElement, callbacks: AvailableEntriesCallbacks) {
        super();
        this.root = root;
        this.callbacks = callbacks;
        this.render();
    }

    private render(): void {
        this.root.innerHTML = this.getHTML();
    }

    public populate(entries: Entry[]): void {
        super.populate(entries);
        this.redraw();
    }

    public redraw(): void {
        const entriesListElement = this.root.querySelector('.available-entries-list');
        if (!entriesListElement) return;

        const entriesList = entriesListElement as HTMLElement;
        entriesList.innerHTML = '';
        this.entries.forEach(entry => {
            const entryElement = this.createEntryElement(entry);
            entriesList.appendChild(entryElement);
        });

        // Re-apply the current search filter after redraw
        if (this.currentSearchString) {
            this.filter(this.currentSearchString);
        }
    }

    private createEntryElement(entry: Entry): HTMLDivElement {
        const entryElement = document.createElement('div');
        const entryName = _(entry.name);
        entryElement.classList.add('entry');
        entryElement.dataset.id = entry.id.toString();
        entryElement.innerHTML = entryName.charAt(0).toLocaleUpperCase() + entryName.slice(1);

        // on click entry
        entryElement.addEventListener('click', () => {
            this.callbacks.onClickEntry(entry);
        });

        return entryElement;
    }

    public filter(searchString: string): void {
        // Store the current search string
        this.currentSearchString = searchString;

        // Check if entries exist in DOM (they might have been replaced by a message)
        let entries = this.root.querySelectorAll('.entry');

        // If no entries exist but we have data, redraw to restore them
        if (entries.length === 0 && this.entries.length > 0) {
            const entriesListElement = this.root.querySelector('.available-entries-list');
            if (entriesListElement) {
                const entriesList = entriesListElement as HTMLElement;
                // Clear any message that might be there
                entriesList.innerHTML = '';
                // Rebuild entries
                this.entries.forEach(entry => {
                    const entryElement = this.createEntryElement(entry);
                    entriesList.appendChild(entryElement);
                });
                // Re-query entries after rebuilding
                entries = this.root.querySelectorAll('.entry');
            }
        }

        let visibleCount = 0;

        entries.forEach(entry => {
            if (
                searchString !== '' &&
                entry.innerHTML.toLowerCase().indexOf(searchString.toLowerCase()) === -1
            ) {
                entry.classList.add('hidden');
            } else {
                entry.classList.remove('hidden');
                visibleCount++;
            }
        });

        // Show "no results" message if search is active and no entries are visible
        if (searchString !== '' && visibleCount === 0) {
            this.showNoResults();
        }
    }

    public showLoading(): void {
        const entriesListElement = this.root.querySelector('.available-entries-list');
        if (!entriesListElement) return;

        const entriesList = entriesListElement as HTMLElement;
        entriesList.innerHTML =
            '<div class="available-entries-message loading">Pobieranie elementów...</div>';
        // Note: We don't reset currentSearchString here because the loading state is temporary
        // and the filter should be reapplied when new data arrives
    }

    public showNoResults(): void {
        const entriesListElement = this.root.querySelector('.available-entries-list');
        if (!entriesListElement) return;

        const entriesList = entriesListElement as HTMLElement;
        entriesList.innerHTML =
            '<div class="available-entries-message no-results">Nie znaleziono elementów dla podanej frazy</div>';
        // Note: We don't reset currentSearchString here because the filter should remain active
    }

    public hideMessages(): void {
        // This is handled by redraw() which clears messages
    }

    private getHTML(): string {
        return `
            <div class="available-entries-list"></div>
        `;
    }
}
